import {
	CurrencyEnum,
	OrderPaymentMethod,
	useCreateFullOrderPaymentMutation,
	useGetFullOrderPaymentPreviewQuery,
} from '@codezo/graphql';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { CheckCircle2, CreditCard, FileText, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { PaymentTotals, money } from './PaymentTotals';

interface PaymentMethodOption {
	method: OrderPaymentMethod;
	available: boolean;
	reason?: string | null;
}

interface PayForFullOrderModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	orderId: string;
	paymentMethods?: PaymentMethodOption[] | null;
	orderPoNumber?: string | null;
	onSuccess?: () => void;
}

type Step = 'summary' | 'checkout' | 'complete';

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';
let stripePromise: ReturnType<typeof loadStripe> | null = null;
function getStripe() {
	if (!stripePromise && STRIPE_KEY) stripePromise = loadStripe(STRIPE_KEY);
	return stripePromise;
}

/**
 * Paying for the whole order in one go — samples and production together.
 *
 * ── Why this is a modal and not a button ──
 *
 * The flow row's price comes from the action's `amount`, which is what the client
 * is QUOTED, in whatever the page is priced in. What they are CHARGED depends on
 * the method — DKK on invoice, EUR on card — and the two routes can legitimately
 * offer different methods, because a full-order total may exceed the card limit
 * where a sample payment does not. So the client picks a method, sees the invoice
 * priced in the currency they will actually be billed in, and only then pays.
 *
 * ── The sample that appears twice ──
 *
 * A prepaid sample is charged in the samples list AND credited against its
 * product's line. Both are rendered, because both are on the invoice. Netting
 * them silently would show a product at a discount nobody could account for,
 * which reads as a pricing error — the backend's words, and they are right.
 */
export function PayForFullOrderModal({
	open,
	onOpenChange,
	orderId,
	paymentMethods,
	orderPoNumber,
	onSuccess,
}: PayForFullOrderModalProps) {
	const stripeOption = useMemo(
		() => paymentMethods?.find((m) => m.method === OrderPaymentMethod.Stripe),
		[paymentMethods]
	);
	const invoiceOption = useMemo(
		() => paymentMethods?.find((m) => m.method === OrderPaymentMethod.Invoice),
		[paymentMethods]
	);

	const defaultMethod = stripeOption?.available
		? OrderPaymentMethod.Stripe
		: OrderPaymentMethod.Invoice;
	const [paymentMethod, setPaymentMethod] = useState<OrderPaymentMethod>(defaultMethod);
	const [createPayment, { loading }] = useCreateFullOrderPaymentMutation();
	const [step, setStep] = useState<Step>('summary');
	const [clientSecret, setClientSecret] = useState<string | null>(null);
	const [poNumber, setPoNumber] = useState(orderPoNumber ?? '');

	useEffect(() => {
		if (open) setPoNumber(orderPoNumber ?? '');
	}, [open, orderPoNumber]);

	// Fixed by method, not by the global selector: Stripe charges EUR, invoice DKK.
	const previewCurrency =
		paymentMethod === OrderPaymentMethod.Stripe ? CurrencyEnum.Eur : CurrencyEnum.Dkk;

	// Previous data held while a method switch refetches, so the breakdown doesn't
	// collapse to a spinner and re-expand on every toggle.
	const {
		data: previewData,
		previousData: prevPreviewData,
		loading: previewLoading,
	} = useGetFullOrderPaymentPreviewQuery({
		variables: { orderId, currency: previewCurrency },
		fetchPolicy: 'no-cache',
		skip: !open,
	});
	const preview =
		previewData?.getFullOrderPaymentPreview ?? prevPreviewData?.getFullOrderPaymentPreview;

	const reset = () => {
		setStep('summary');
		setClientSecret(null);
		setPaymentMethod(defaultMethod);
		setPoNumber(orderPoNumber ?? '');
	};

	const handleClose = (next: boolean) => {
		if (!next) {
			if (step === 'complete') onSuccess?.();
			reset();
		}
		onOpenChange(next);
	};

	const handleContinue = async () => {
		try {
			const { data } = await createPayment({
				variables: {
					input: {
						orderId,
						paymentMethod,
						poNumber: poNumber.trim() || undefined,
						returnUrl: window.location.href,
					},
				},
			});

			const result = data?.createFullOrderPayment;
			if (!result?.success) {
				toast.error(result?.message || 'Failed to create payment');
				return;
			}

			if (result.clientSecret) {
				setClientSecret(result.clientSecret);
				setStep('checkout');
			} else {
				toast.success(result.message || 'Invoice booked. You will receive it by email.');
				handleClose(false);
				onSuccess?.();
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create payment');
		}
	};

	const credited = (preview?.items ?? []).reduce((sum, l) => sum + l.deductibleAmount, 0);

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-lg"
				showCloseButton={step !== 'checkout'}
				{...(step === 'checkout'
					? {
							onEscapeKeyDown: (e: Event) => e.preventDefault(),
							onPointerDownOutside: (e: Event) => e.preventDefault(),
						}
					: {})}
			>
				<DialogHeader>
					<DialogTitle>
						{step === 'summary' && 'Pay for the whole order'}
						{step === 'checkout' && 'Checkout'}
						{step === 'complete' && 'Payment confirmed'}
					</DialogTitle>
				</DialogHeader>

				<DialogBody className="scroll-thin max-h-[70vh] overflow-y-auto p-0">
					{step === 'summary' && (
						<div className="flex flex-col">
							<div className="border-b border-line-1 px-4 py-3">
								<p className="text-sm leading-relaxed text-text-3">
									One payment covering samples and production. Sampling still
									happens and you still approve each sample — what this removes is
									paying again afterwards.
								</p>
							</div>

							<div className="border-b border-line-1 px-4 py-3">
								<div className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
									Payment method
								</div>
								<div className="flex gap-1.5">
									<button
										type="button"
										disabled={stripeOption?.available === false}
										onClick={() => setPaymentMethod(OrderPaymentMethod.Stripe)}
										className={cn(
											'flex flex-1 items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium transition-colors',
											paymentMethod === OrderPaymentMethod.Stripe
												? 'border-accent bg-accent-soft text-accent'
												: 'border-line-2 text-text-3 hover:border-line-3',
											stripeOption?.available === false &&
												'cursor-not-allowed opacity-40'
										)}
									>
										<CreditCard size={14} />
										Card (EUR)
									</button>
									<button
										type="button"
										disabled={invoiceOption?.available === false}
										onClick={() => setPaymentMethod(OrderPaymentMethod.Invoice)}
										className={cn(
											'flex flex-1 items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium transition-colors',
											paymentMethod === OrderPaymentMethod.Invoice
												? 'border-accent bg-accent-soft text-accent'
												: 'border-line-2 text-text-3 hover:border-line-3',
											invoiceOption?.available === false &&
												'cursor-not-allowed opacity-40'
										)}
									>
										<FileText size={14} />
										Invoice (DKK)
									</button>
								</div>
								{/* A full-order total can exceed the card limit where a
								    sample payment does not, so this reason is worth showing
								    rather than just greying the button. */}
								{stripeOption?.available === false && stripeOption.reason && (
									<p className="mt-2 text-xs text-status-warn">
										Card: {stripeOption.reason}
									</p>
								)}
								{invoiceOption?.available === false && invoiceOption.reason && (
									<p className="mt-2 text-xs text-status-warn">
										Invoice: {invoiceOption.reason}
									</p>
								)}
							</div>

							<div className="border-b border-line-1 px-4 py-3">
								<label
									htmlFor="full-order-po-number"
									className="mb-2 block text-xs font-medium uppercase tracking-[0.06em] text-text-4"
								>
									PO number{' '}
									<span className="text-text-4 normal-case">(optional)</span>
								</label>
								<Input
									id="full-order-po-number"
									value={poNumber}
									onChange={(e) => setPoNumber(e.target.value)}
									placeholder="e.g. PO-12345"
								/>
							</div>

							{!preview && previewLoading ? (
								<div className="flex items-center justify-center py-10">
									<Loader2 size={20} className="animate-spin text-text-3" />
								</div>
							) : preview ? (
								<div
									className={cn(
										'transition-opacity',
										previewLoading && 'opacity-50'
									)}
								>
									<div className="border-b border-line-1">
										<div className="px-4 pt-3 pb-1 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
											Products ({preview.items.length})
										</div>
										{preview.items.map((line) => (
											<div key={line.orderItemId} className="px-4 py-2">
												<div className="flex items-baseline justify-between gap-3 text-sm">
													<span className="min-w-0 truncate text-text-2">
														{line.description}
													</span>
													<span className="shrink-0 text-text-2">
														{preview.currency}{' '}
														{money(line.lineSubtotal)}
													</span>
												</div>
												{/* The credit side of a prepaid sample. Shown as
												    its own negative line, because that is how it
												    reaches the invoice. */}
												{line.deductibleAmount > 0 && (
													<div className="flex items-baseline justify-between gap-3 text-xs text-status-success">
														{/* NOT "already paid". On this route the sample
														    is charged in the Samples list on THIS
														    invoice and credited straight back against
														    its product — the two cancel. The wording
														    was borrowed from the production preview,
														    where `deductibleAmount` really is a sample
														    paid earlier; here it would tell someone
														    who has paid nothing that they had. */}
														<span>
															Sample charged below — credited here
														</span>
														<span className="shrink-0">
															−{preview.currency}{' '}
															{money(line.deductibleAmount)}
														</span>
													</div>
												)}
											</div>
										))}
									</div>

									{preview.samples.length > 0 && (
										<div className="border-b border-line-1">
											<div className="px-4 pt-3 pb-1 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Samples ({preview.samples.length})
											</div>
											{preview.samples.map((line) => (
												<div
													key={line.sampleId}
													className="flex items-baseline justify-between gap-3 px-4 py-2 text-sm"
												>
													<span className="min-w-0 truncate text-text-2">
														{line.description}
													</span>
													<span className="shrink-0 text-text-2">
														{preview.currency}{' '}
														{money(
															line.sampleAmount + line.shippingAmount
														)}
													</span>
												</div>
											))}
											{credited > 0 && (
												<p className="px-4 pb-2 text-xs leading-relaxed text-text-4">
													The samples above are charged on this invoice
													and credited back against their product on the
													same invoice — they cost nothing net. Both sides
													are shown because both are on the document.
												</p>
											)}
										</div>
									)}

									{preview.deliveries.length > 0 && (
										<div className="border-b border-line-1">
											<div className="px-4 pt-3 pb-1 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
												Delivery
											</div>
											{preview.deliveries.map((d, i) => (
												<div
													key={`${d.method}-${i}`}
													className="flex items-baseline justify-between gap-3 px-4 py-2 text-sm"
												>
													<span className="text-text-2">{d.method}</span>
													<span className="shrink-0 text-text-2">
														{preview.currency} {money(d.amount)}
													</span>
												</div>
											))}
										</div>
									)}

									{/* Paid sample credit this invoice cannot absorb. The
									    client is genuinely owed it and there is no credit
									    note to recover it with, so it is stated plainly
									    rather than rounded into the total. */}
									{preview.deductionsUncredited > 0 && (
										<div className="border-b border-line-1 px-4 py-3">
											<div className="flex items-baseline justify-between gap-3 text-sm">
												<span className="text-status-warn">
													Sample credit not applied
												</span>
												<span className="shrink-0 text-status-warn">
													{preview.currency}{' '}
													{money(preview.deductionsUncredited)}
												</span>
											</div>
											<p className="mt-1 text-xs leading-relaxed text-text-4">
												More sample credit than this invoice can absorb. You
												are owed this — please contact us and we will sort
												it out.
											</p>
										</div>
									)}

									<PaymentTotals
										currency={preview.currency}
										grandTotal={preview.grandTotal}
										vatRate={preview.vatRate}
										vatAmount={preview.vatAmount}
										grossAmount={preview.grossAmount}
									/>
								</div>
							) : null}
						</div>
					)}

					{step === 'checkout' && clientSecret && (
						<div className="px-1 py-1">
							<EmbeddedCheckoutProvider
								stripe={getStripe()}
								options={{ clientSecret, onComplete: handleCheckoutComplete }}
							>
								<EmbeddedCheckout />
							</EmbeddedCheckoutProvider>
						</div>
					)}

					{step === 'complete' && (
						<div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
							<CheckCircle2 size={28} className="text-status-success" />
							<div className="text-base font-medium text-text-1">
								Your order is paid
							</div>
							<div className="max-w-sm text-sm leading-relaxed text-text-3">
								Sampling carries on as normal — you will still be asked to approve
								each sample. Approved items go straight into production with nothing
								further to pay.
							</div>
						</div>
					)}
				</DialogBody>

				{step === 'summary' && (
					<DialogFooter>
						<Button variant="ghost" onClick={() => handleClose(false)}>
							Cancel
						</Button>
						<Button onClick={handleContinue} disabled={loading || !preview}>
							{loading
								? 'Working…'
								: paymentMethod === OrderPaymentMethod.Stripe
									? 'Continue to payment'
									: 'Book invoice'}
						</Button>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	);

	function handleCheckoutComplete() {
		setStep('complete');
		onSuccess?.();
	}
}
